import React, { useCallback, useEffect, useRef, useState } from "react";
import { useDispatch } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import {
  Box,
  Button,
  CircularProgress,
  Container,
  Typography,
} from "@material-ui/core";
import { isEmpty } from "lodash";
import { toast } from "react-toastify";

import { deleteVenue, createVenue, updateVenue } from "./sliceReducer";
import {
  DELETE_VENUE,
  CREATE_VENUE,
  EDIT_VENUE,
  GET_STATES_BY_COUNTRY_ISO,
} from "../../constants/endpoints";
import { VENUE_FIELDS_TITLE } from "../../constants/data";
import { TITLE_BUTTONS, PAGES_TITLES, TITLES } from "../../constants/titles";
import { getFillDtoVenues } from "../../utils/getFillDto";
import { venueAdminApi } from "../../utils/venueAdminApi";
import { getList } from "../../dataProvider/API";
import { keepState } from "../../utils/returnTo";
import { MAPS_TYPES } from "../../utils/mapsAutofill";

import { useRuleStyles } from "../Rules/styles";
import {
  SEARCH_KEYS,
  clearedFilters,
  hasFilters,
  readQuery,
  searchDrafts,
  toApiParams,
  writeQuery,
} from "./venueListQuery";
import VenueFilters from "./VenueFilters";
import VenuesTable from "./VenuesTable";
import ConfirmDialog from "../../components/ConfirmDialog";
import FieldOfCreate from "../../components/FieldOfCreate";
import ButtonsGroup from "../../components/ButtonsGroup";
import CreateFromLinkDialog from "../../containers/CreateFromLinkDialog";

const SEARCH_DEBOUNCE_MS = 350;
const EMPTY_PAGE = { dtos: [], totalElements: 0, currentPage: 0 };

// The create/edit form shows every venue field; only the table dropped some.
const dataForForms = Object.entries(VENUE_FIELDS_TITLE).filter(
  ([key]) => key !== "id",
);

/** The form takes the address fields flat; `venues/{id}` nests them under `addressDto`. */
export const normalizeVenueDefaultValue = (dto) => {
  const v = dto || {};
  const a = v.addressDto || {};

  return {
    ...v,
    ...a,
    id: v.id,
    state: typeof a.state === "string" ? a.state.trim() : a.state,
    addressDto: a,
  };
};

const Venues = () => {
  const classes = useRuleStyles();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();

  // ---- list state: filters, sort and page live in the URL --------------------------------
  const [listState, setListState] = useState(() => readQuery(location.search));
  const [drafts, setDrafts] = useState(() => searchDrafts(listState));
  const [result, setResult] = useState(EMPTY_PAGE);
  const [listLoading, setListLoading] = useState(false);
  const [countries, setCountries] = useState([]);
  const [states, setStates] = useState([]);
  const [statesLoading, setStatesLoading] = useState(false);
  const requestSeq = useRef(0);

  const dtos = result.dtos || [];

  const [open, setOpen] = useState(false);
  const [formLoading, setFormLoading] = useState(false);
  const [defaultValueForm, setDefaultValueForm] = useState(null);
  const [selectedDto, setSelectedDto] = useState({});
  const [openModal, setOpenModal] = useState(false);
  const [selectID, setSelectID] = useState(null);
  const [openFromLink, setOpenFromLink] = useState(false);

  const isEditMode = !isEmpty(selectedDto);

  // Only the latest request may update the table, so a slow page can't overwrite a newer one.
  const reloadVenues = useCallback(() => {
    const seq = ++requestSeq.current;
    setListLoading(true);
    return venueAdminApi
      .list(toApiParams(listState))
      .then((data) => {
        if (seq === requestSeq.current) setResult(data || EMPTY_PAGE);
      })
      .catch((e) => {
        if (seq === requestSeq.current) toast.error(e.message);
      })
      .finally(() => {
        if (seq === requestSeq.current) setListLoading(false);
      });
  }, [listState]);

  // The column search boxes commit together, 350 ms after the last keystroke; any change
  // resets the page.
  useEffect(() => {
    const handle = setTimeout(() => {
      setListState((prev) => {
        const changes = {};
        SEARCH_KEYS.forEach((key) => {
          const value = String(drafts[key] || "").trim();
          if (value !== prev[key]) changes[key] = value;
        });
        return isEmpty(changes) ? prev : { ...prev, ...changes, page: 0 };
      });
    }, SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(handle);
  }, [drafts]);

  const onSearch = (key, value) =>
    setDrafts((prev) => ({ ...prev, [key]: value }));

  const patchList = (changes) =>
    setListState((prev) => ({ ...prev, page: 0, ...changes }));

  const onSort = (key) =>
    setListState((prev) => ({
      ...prev,
      page: 0,
      sort: key,
      isAscending: prev.sort === key ? !prev.isAscending : true,
    }));

  const onPage = ({ page, limit }) =>
    setListState((prev) => {
      const size = Number(limit) || prev.size;
      return { ...prev, size, page: size === prev.size ? page : 0 };
    });

  const clearFilters = () => {
    setListState((prev) => {
      const next = clearedFilters(prev);
      setDrafts(searchDrafts(next));
      return next;
    });
  };

  // ---- create / edit ---------------------------------------------------------------------
  const handleClose = () => {
    setDefaultValueForm(null);
    setOpen(false);
    setFormLoading(false);
    setSelectedDto({});
  };

  /**
   * Edit loads the full venue first: the list row has no email, backup email or mobile
   * phone, and the form must still show (and keep) them.
   */
  const handleClickOpen = (row) => {
    setOpen(true);

    if (!row?.id) {
      setSelectedDto({});
      setDefaultValueForm({});
      return;
    }

    setFormLoading(true);
    setDefaultValueForm(null);
    venueAdminApi
      .get(row.id)
      .then((dto) => {
        if (!dto?.id) throw new Error(`Venue ${row.id} was not found.`);
        setSelectedDto(dto);
        setDefaultValueForm(normalizeVenueDefaultValue(dto));
      })
      .catch((e) => {
        toast.error(e.message);
        handleClose();
      })
      .finally(() => setFormLoading(false));
  };

  const handleOpenFromLink = () => setOpenFromLink(true);
  const handleCloseFromLink = () => setOpenFromLink(false);

  /**
   * Receives the drafts from CreateFromLinkDialog and opens the normal create form
   * pre-filled. Nothing has been persisted at this point: the admin still reviews
   * and submits through the existing createVenue path.
   */
  const handleApplyFromLink = ({ venueValues }) => {
    setOpenFromLink(false);

    if (!venueValues) return;

    setSelectedDto({});
    setDefaultValueForm(venueValues);
    setOpen(true);
  };

  const handleOpenModal = (row) => {
    setSelectID(row?.id ?? null);
    setOpenModal(true);
  };

  const handleCloseModal = () => {
    setSelectID(null);
    setOpenModal(false);
  };

  // IMPORTANT: return promise + unwrap so ButtonsGroup can await and only close on success
  const deleteOneVenue = () => {
    if (!selectID) {
      return Promise.reject(new Error("No venue selected for deletion."));
    }

    return dispatch(deleteVenue(DELETE_VENUE(selectID)))
      .unwrap()
      .then(() => reloadVenues());
  };

  const createOneVenue = (params) =>
    dispatch(
      createVenue({
        endpoint: CREATE_VENUE,
        data: getFillDtoVenues(params),
      }),
    )
      .unwrap()
      .then(() => reloadVenues());

  const updateOneVenue = (params, id, idAddress) =>
    dispatch(
      updateVenue({
        endpoint: EDIT_VENUE,
        data: { ...getFillDtoVenues(params, idAddress), id },
      }),
    )
      .unwrap()
      .then(() => reloadVenues());

  // IMPORTANT: do NOT close modal until create/update succeeds
  const onSubmit = (params) => {
    if (isEditMode) {
      const { id } = selectedDto;
      const idAddress = selectedDto?.addressDto?.id;

      if (!id || !idAddress) {
        return Promise.reject(
          new Error("Cannot update venue: missing venue id or address id."),
        );
      }

      return updateOneVenue(params, id, idAddress).then(() => {
        handleClose();
      });
    }

    return createOneVenue(params).then(() => {
      handleClose();
    });
  };

  // ---- effects ---------------------------------------------------------------------------
  useEffect(() => {
    venueAdminApi
      .filterOptions()
      .then((options) => setCountries(options?.countries || []))
      .catch((e) => toast.error(e.message));
  }, []);

  // States of the chosen country, for the State filter.
  useEffect(() => {
    if (!listState.country) {
      setStates([]);
      return undefined;
    }
    let current = true;
    setStatesLoading(true);
    getList(GET_STATES_BY_COUNTRY_ISO(listState.country))
      .then((list) => current && setStates(Array.isArray(list) ? list : []))
      .catch(() => current && setStates([]))
      .finally(() => current && setStatesLoading(false));
    return () => {
      current = false;
    };
  }, [listState.country]);

  // Load, then mirror the list state into the URL.
  useEffect(() => {
    reloadVenues();
    const nextSearch = writeQuery(listState);
    if (nextSearch !== location.search) {
      navigate(
        { pathname: location.pathname, search: nextSearch },
        { replace: true, state: keepState(location) },
      );
    }
    // location is intentionally not a dependency: this effect writes it.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [listState]);

  // The URL changed from outside (nav link, back button, a pasted link): follow it.
  useEffect(() => {
    const fromUrl = readQuery(location.search);
    if (writeQuery(fromUrl) === writeQuery(listState)) return;
    setListState(fromUrl);
    setDrafts(searchDrafts(fromUrl));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.search]);

  const total = result.totalElements || 0;

  return (
    <Box className={classes.page}>
      <Container maxWidth={false}>
        <Box className={classes.header}>
          <Box>
            <Typography component="h1" className={classes.title}>
              {PAGES_TITLES.VENUES}
            </Typography>
            <Typography className={classes.subtitle} aria-live="polite">
              {listLoading && !total
                ? "Loading venues…"
                : `Showing ${total} venue${total === 1 ? "" : "s"}`}
            </Typography>
          </Box>
          <Box display="flex" style={{ gap: 12 }} flexWrap="wrap">
            <Button
              variant="outlined"
              className={classes.touch}
              onClick={handleOpenFromLink}
            >
              {TITLE_BUTTONS.CREATE_FROM_LINK}
            </Button>
            <Button
              variant="contained"
              color="primary"
              className={classes.touch}
              onClick={() => handleClickOpen()}
            >
              {TITLE_BUTTONS.CREATE_VENUE}
            </Button>
          </Box>
        </Box>

        <VenueFilters
          state={listState}
          patch={patchList}
          countries={countries}
          states={states}
          statesLoading={statesLoading}
          canClear={hasFilters(listState)}
          onClear={clearFilters}
        />

        <VenuesTable
          rows={dtos}
          state={listState}
          drafts={drafts}
          total={total}
          loading={listLoading}
          onSort={onSort}
          onSearch={onSearch}
          onPage={onPage}
          onEdit={handleClickOpen}
          onDelete={handleOpenModal}
        />
      </Container>

      {open && (
        <ConfirmDialog
          open
          handleClose={handleClose}
          title={isEditMode ? TITLES.EDIT_VENUE : TITLES.CREATE_VENUE}
        >
          {formLoading ? (
            <Box className={classes.progressWrap}>
              <CircularProgress aria-label="Loading venue" />
            </Box>
          ) : !!defaultValueForm ? (
            <FieldOfCreate
              dataForForms={dataForForms}
              defaultValue={defaultValueForm}
              handleClose={handleClose}
              onSubmit={onSubmit}
            />
          ) : null}
        </ConfirmDialog>
      )}

      {openFromLink && (
        <ConfirmDialog
          open
          handleClose={handleCloseFromLink}
          title={TITLES.CREATE_VENUE_FROM_LINK}
          disableBackdropClose
        >
          <CreateFromLinkDialog
            open
            type={MAPS_TYPES.VENUE}
            onCancel={handleCloseFromLink}
            onApply={handleApplyFromLink}
          />
        </ConfirmDialog>
      )}

      {openModal && (
        <ConfirmDialog
          open
          handleClose={handleCloseModal}
          title={TITLES.DELETE_MODAL}
        >
          <ButtonsGroup
            handleClose={handleCloseModal}
            handleFunction={deleteOneVenue}
            submitTitle={TITLE_BUTTONS.DELETE}
            closeTitle={TITLE_BUTTONS.CANCEL}
          />
        </ConfirmDialog>
      )}
    </Box>
  );
};

export default Venues;
